/**
 * FP3-122 — Canonical Goal Average calculation.
 *
 * Contract (from user spec, Feb 9, 2026):
 *   For each distinct targeted session that produced numeric data
 *   on this goal:
 *     session_percentage = sum(correct trials for this goal in this session)
 *                          ---------------------------------------
 *                          sum(total trials for this goal in this session)
 *   goal_average = arithmetic mean of those session percentages
 *   n_sessions   = number of sessions that contributed
 *
 * Example (user):
 *   85.7% (12/14) + 54.5% (6/11) across 2 targeted sessions
 *   average = (85.7 + 54.5) / 2 = 70.1
 *
 * We do NOT pool every trial across all sessions into one percentage.
 *
 * A "session" here is identified by:
 *   trial_runs   → `session_id`
 *   goal_evidence → `linked_session_id`
 *   If neither is populated (rare — direct manual entry without a
 *   linked session), we treat the row's own id as a stand-alone
 *   "session" so a single manual entry still contributes exactly
 *   once — never absorbed into a peer.
 *
 * Superseded rows (see FP3-122 correction-history) are filtered at
 * the API layer already. This helper is defensive: it also skips
 * any row that carries `superseded: true`.
 */

// Compute the goal-average from the trial_runs + goal_evidence arrays
// already available on the student payload (GET /students/{id}).
// Returns null-shape ({ average: null, sessionCount: 0 }) when there
// is no numeric session-level data — the caller decides how to
// render (e.g. "—" or hide the chip).
export function computeGoalAverage(goalId, trialRuns = [], goalEvidence = []) {
  if (!goalId) return { average: null, sessionCount: 0 };

  // Map: session key → { correct, total }
  const perSession = new Map();

  const bump = (key, correct, total) => {
    if (!Number.isFinite(correct) || !Number.isFinite(total) || total <= 0) return;
    const cur = perSession.get(key) || { correct: 0, total: 0 };
    cur.correct += correct;
    cur.total += total;
    perSession.set(key, cur);
  };

  for (const tr of trialRuns || []) {
    if (!tr || tr.goal_id !== goalId) continue;
    if (tr.superseded === true) continue;
    // trial_runs are always live-scored rows; treat them as
    // numeric data even when both counts are 0 → skipped by bump.
    const key = tr.session_id ? `s:${tr.session_id}` : `tr:${tr.id}`;
    bump(key, Number(tr.correct_count), Number(tr.total_trials));
  }

  for (const ev of goalEvidence || []) {
    if (!ev || ev.goal_id !== goalId) continue;
    if (ev.superseded === true) continue;
    // Only rows with numeric ratios count toward the average.
    // Narrative-only entries carry no numerator/denominator.
    const n = Number(ev.numerator);
    const d = Number(ev.denominator);
    if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) continue;
    // percent_trials + informal_assessment (chartable) both count.
    // We intentionally include informal_assessment rows even when
    // `chartable=false` — the canonical average is a data question,
    // not a chart-render question. If it has a valid ratio, it
    // targeted the goal on that session and must contribute.
    const key = ev.linked_session_id
      ? `s:${ev.linked_session_id}`
      : `ev:${ev.id}`;
    bump(key, n, d);
  }

  if (perSession.size === 0) {
    return { average: null, sessionCount: 0 };
  }

  let sum = 0;
  for (const { correct, total } of perSession.values()) {
    sum += (100 * correct) / total;
  }
  const average = sum / perSession.size;
  // One decimal place — matches "70.1%" example from the spec.
  return {
    average: Math.round(average * 10) / 10,
    sessionCount: perSession.size,
  };
}

// Convenience formatter: "70.1% · 2 sessions".
export function formatGoalAverage({ average, sessionCount } = {}) {
  if (average === null || average === undefined || sessionCount === 0) {
    return "— · 0 sessions";
  }
  return `${average.toFixed(1)}% · ${sessionCount} session${sessionCount === 1 ? "" : "s"}`;
}

// FP3-130A — Compact percent formatter used by Progress surfaces
// that display the canonical goal average / most-recent point as
// a single value (no session-count caption).
//
// Contract (locked with user, Feb 2026):
//   `null` / `undefined` / no numeric session data → "—" (no data)
//   `0` → "0%"                                     (measured zero)
//   otherwise → "XX%" with sensible rounding.
export function formatCanonicalPercent(value, { decimals = 0 } = {}) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(decimals)}%`;
}
